import { call } from "../components/Dialer/services/sipService";
import {getAudioFlags} from "./abTest/variant";
import {prewarmBackgroundedAudioWorklet} from "../utilis/backgroundedAudioWorklet";
import {RootState, useAppDispatch} from "../store/store";
import {
    getSelectedSpeaker, navigateDialer,
    selectSelectedPhoneNumber,
    setCallFrom,
    setCallState,
    setMicRequired,
    setPhoneNumber,
    playRingback,
    stopRingback,
    stopConnectingSound,
    getIsRingbackPlaying,
    getIsConnectingSoundPlaying,
    tagEngine
} from "../store/features/calls/dialerSlice";
import {CallStateEnum} from "../components/Dialer/callState";
import {useCallOptions} from "../components/Dialer/hooks/useCallOptions";
import {useSelector} from "react-redux";
import {selectUserId} from "../store/features/Auth/authSlice";
import {CALL_CONNECTING_SOUND, CALL_RINGBACK_SOUND} from '../assets/sounds/callSounds';
import { useEffect, useMemo} from "react";
import {useLazyGetContactByPhoneNumberQuery} from "../store/features/Contacts/contactsService";
import {showToast} from "../store/features/Notifications/notificationsSlice";
import {blockCallDuringGroupCall} from "./groupCallBusyGuard";
import {User, UserElement} from "../store/features/Users/userService.ts";
import {UserSettings} from "../store/features/ProfileSettings/profileSettingsService.ts";

interface CallOptions {
    msisdn: string;
    callTo?: User | UserElement | UserSettings | string;
    isInternal?: boolean;
}

/**
 * Plain JS guard against concurrent makeCall() — React's useSelector(callState)
 * is stale within the same render frame, so two rapid clicks both see 'IDLE'.
 * Reset to false when callState transitions away from CONNECTING (via useEffect).
 */
let makeCallInProgress = false;

/** Reset the makeCall guard — exported so sipService ended/failed handlers can call it too. */
export const resetMakeCallGuard = () => { makeCallInProgress = false; };

class LoopingSoundManager {
    private audio: HTMLAudioElement;
    private isPlaying: boolean = false;

    protected constructor(source: string, volume: number = 1) {
        this.audio = new Audio(source);
        this.audio.loop = true;
        this.audio.volume = volume;

        this.audio.addEventListener('play', () => {
            this.isPlaying = true;
        });

        this.audio.addEventListener('pause', () => {
            this.isPlaying = false;
        });

        this.audio.addEventListener('ended', () => {
            this.isPlaying = false;
        });
    }

    public play(): void {
        if (!this.isPlaying && this.audio.paused) {
            this.audio.currentTime = 0;
            this.audio.play().catch(() => {
                this.isPlaying = false;
            });
        }
    }

    public stop(): void {
        if (!this.audio.paused) {
            this.audio.pause();
            this.audio.currentTime = 0;
        }
        this.isPlaying = false;
    }

    public getIsPlaying(): boolean {
        return this.isPlaying;
    }
}

const CONNECTING_SOUND_VOLUME = 0.25;

export class RingbackManager extends LoopingSoundManager {
    private static instance: RingbackManager;

    private constructor() {
        super(CALL_RINGBACK_SOUND);
    }

    public static getInstance(): RingbackManager {
        if (!RingbackManager.instance) {
            RingbackManager.instance = new RingbackManager();
        }
        return RingbackManager.instance;
    }
}

export class ConnectingSoundManager extends LoopingSoundManager {
    private static instance: ConnectingSoundManager;

    private constructor() {
        super(CALL_CONNECTING_SOUND, CONNECTING_SOUND_VOLUME);
    }

    public static getInstance(): ConnectingSoundManager {
        if (!ConnectingSoundManager.instance) {
            ConnectingSoundManager.instance = new ConnectingSoundManager();
        }
        return ConnectingSoundManager.instance;
    }
}

export const useCallService = () => {
    const dispatch = useAppDispatch();
    const audioDevice = useSelector(getSelectedSpeaker);
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const isRingbackPlaying = useSelector(getIsRingbackPlaying);
    const isConnectingSoundPlaying = useSelector(getIsConnectingSoundPlaying);
    const options = useCallOptions();
    const selectedPhoneNumber = useSelector(selectSelectedPhoneNumber);
    const userId = useSelector(selectUserId);
    const [fetchContact] = useLazyGetContactByPhoneNumberQuery();

    const ringbackManager = useMemo(() => RingbackManager.getInstance(), []);
    const connectingSoundManager = useMemo(() => ConnectingSoundManager.getInstance(), []);

    useEffect(() => {
        if (isRingbackPlaying) {
            ringbackManager.play();
        } else {
            ringbackManager.stop();
        }
    }, [isRingbackPlaying, ringbackManager]);

    useEffect(() => {
        if (isConnectingSoundPlaying) {
            connectingSoundManager.play();
        } else {
            connectingSoundManager.stop();
        }
    }, [isConnectingSoundPlaying, connectingSoundManager]);

    useEffect(() => {
        if (isRingbackPlaying && callState !== CallStateEnum.CONNECTING) {
            dispatch(stopRingback());
        }
    }, [callState, isRingbackPlaying, dispatch]);

    useEffect(() => {
        if (isConnectingSoundPlaying && callState !== CallStateEnum.CONNECTING) {
            dispatch(stopConnectingSound());
        }
    }, [callState, isConnectingSoundPlaying, dispatch]);

    // Reset the makeCall guard when callState returns to IDLE (call ended/failed).
    useEffect(() => {
        if (callState === CallStateEnum.IDLE) {
            makeCallInProgress = false;
        }
    }, [callState]);

    const makeCall = async ({ msisdn, callTo, isInternal = false }: CallOptions) => {
        if (!msisdn) return;
        if (blockCallDuringGroupCall(callState, dispatch)) return;

        // Guard against double-click: React useSelector(callState) is stale within
        // the same render frame, so two rapid clicks both see 'IDLE' even after
        // the first dispatch(setCallState(CONNECTING)). Use a plain JS variable
        // that updates synchronously — _callInProgress in sipService.call() catches
        // the race at the SIP layer, but we also need to block duplicate makeCall()
        // invocations to avoid duplicate getUserMedia/fetchContact side effects.
        if (makeCallInProgress) return;
        makeCallInProgress = true;

        // Must run before any await: Chrome's transient activation does not
        // survive getUserMedia / fetchContact, and ctx.resume() / addModule()
        // park without it. Skip on macOS — AudioContext.resume() before
        // getUserMedia silently leaves the WebRTC mic sender producing no
        // samples (tenant bcownms call 210 / 211 — audio_stuck_outbound
        // throughout despite a healthy PC).
        if (getAudioFlags().backgroundedAudioWorklet && !isMacOs()) {
            prewarmBackgroundedAudioWorklet(`prewarm-${Date.now()}`);
        }

        if (!selectedPhoneNumber && !isInternal) {
            dispatch(navigateDialer('/error/no-number'));
            return;
        }

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!mediaStream) {
                throw new Error('No microphone access');
            }
            mediaStream.getTracks().forEach(t => t.stop());
        } catch (error) {
            dispatch(setMicRequired(true));
            dispatch(showToast({
                description: 'Microphone is turned off. Turn it on to use this feature.',
                type: 'error',
                dontHide: true,
                position: 'center',
                icon: 'mic_off'
            }));
            return;
        }

        const sanitizedMsisdn = isInternal ? msisdn : msisdn.replace(/\+/g, '');
        let callFrom: unknown = callTo || msisdn;
        if (!isInternal) {
            const { data = [] } = await fetchContact({ msisdn: sanitizedMsisdn, userId });
            callFrom = data[0]?.primary ? {...data[0].primary, msisdn: sanitizedMsisdn} : msisdn;
        }

        tagEngine('sip', dispatch)(setCallFrom(callFrom));
        tagEngine('sip', dispatch)(setCallState(CallStateEnum.CONNECTING));
        dispatch(setPhoneNumber(''));

        if (isInternal) {
            dispatch(playRingback());
        }

        call(msisdn, {
            ...options,
            fromUserName: isInternal ? userId : selectedPhoneNumber.msisdn,
        }, audioDevice);
    };

    return {
        makeCall,
    };
};

function isMacOs(): boolean {
    try {
        return typeof navigator !== 'undefined' && /Mac OS X|Macintosh/i.test(navigator.userAgent);
    } catch {
        return false;
    }
}